import anime from 'animejs';

/**
 * AnimationManager - Centralized anime.js timeline and animation management
 * Handles performance monitoring, reduced motion support, and reusable presets
 */
class AnimationManager {
  constructor() {
    this.timelines = new Map();
    this.animations = new Map();
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    // Listen for reduced motion preference changes
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
      this.prefersReducedMotion = e.matches;
    });
  }

  /**
   * Check if animations should be disabled
   */
  shouldAnimate() {
    return !this.prefersReducedMotion;
  }

  /**
   * Create a new timeline
   */
  createTimeline(id, options = {}) {
    if (this.timelines.has(id)) {
      this.timelines.get(id).pause();
    }
    
    const timeline = anime.timeline({
      autoplay: this.shouldAnimate(),
      ...options
    });
    
    this.timelines.set(id, timeline);
    return timeline;
  }

  /**
   * Get existing timeline
   */
  getTimeline(id) {
    return this.timelines.get(id);
  }

  /**
   * Remove and cleanup timeline
   */
  removeTimeline(id) {
    const timeline = this.timelines.get(id);
    if (timeline) {
      timeline.pause();
      this.timelines.delete(id);
    }
  }

  /**
   * Create and store animation
   */
  createAnimation(id, config) {
    if (!this.shouldAnimate()) {
      return null;
    }

    if (this.animations.has(id)) {
      this.animations.get(id).pause();
    }

    const animation = anime(config);
    this.animations.set(id, animation);
    return animation;
  }

  /**
   * Remove animation
   */
  removeAnimation(id) {
    const animation = this.animations.get(id);
    if (animation) {
      animation.pause();
      this.animations.delete(id);
    }
  }

  /**
   * Cleanup all animations and timelines
   */
  cleanup() {
    this.timelines.forEach(timeline => timeline.pause());
    this.animations.forEach(animation => animation.pause());
    this.timelines.clear();
    this.animations.clear();
  }

  // ===== PRESET ANIMATIONS =====

  /**
   * Terminal boot sequence animation
   */
  bootSequence(targets, options = {}) {
    if (!this.shouldAnimate()) return null;

    return anime({
      targets,
      opacity: [0, 1],
      scale: [0.8, 1],
      duration: 800,
      easing: 'easeOutElastic(1, .6)',
      ...options
    });
  }

  /**
   * Fade in animation
   */
  fadeIn(targets, options = {}) {
    if (!this.shouldAnimate()) return null;

    return anime({
      targets,
      opacity: [0, 1],
      duration: 600,
      easing: 'easeOutQuad',
      ...options
    });
  }

  /**
   * Slide in from left
   */
  slideInLeft(targets, options = {}) {
    if (!this.shouldAnimate()) return null;

    return anime({
      targets,
      translateX: [-20, 0],
      opacity: [0, 1],
      duration: 400,
      easing: 'easeOutCubic',
      ...options
    });
  }

  /**
   * Glitch effect for errors
   */
  glitch(targets, options = {}) {
    if (!this.shouldAnimate()) return null;

    return anime({
      targets,
      translateX: [
        { value: -5, duration: 50 },
        { value: 5, duration: 50 },
        { value: -3, duration: 50 },
        { value: 3, duration: 50 },
        { value: 0, duration: 50 }
      ],
      opacity: [
        { value: 0.7, duration: 50 },
        { value: 1, duration: 50 }
      ],
      loop: 2,
      easing: 'linear',
      ...options
    });
  }

  /**
   * Pulse animation
   */
  pulse(targets, options = {}) {
    if (!this.shouldAnimate()) return null;

    return anime({
      targets,
      scale: [1, 1.05, 1],
      duration: 1000,
      loop: true,
      easing: 'easeInOutQuad',
      ...options
    });
  }

  /**
   * Glow pulse animation
   */
  glowPulse(targets, options = {}) {
    if (!this.shouldAnimate()) return null;

    return anime({
      targets,
      boxShadow: [
        { value: '0 0 5px rgba(0, 255, 65, 0.3)' },
        { value: '0 0 20px rgba(0, 255, 65, 0.6)' },
        { value: '0 0 5px rgba(0, 255, 65, 0.3)' }
      ],
      duration: 2000,
      loop: true,
      easing: 'easeInOutQuad',
      ...options
    });
  }

  /**
   * Stagger text reveal
   */
  staggerText(targets, options = {}) {
    if (!this.shouldAnimate()) return null;

    return anime({
      targets,
      opacity: [0, 1],
      translateY: [20, 0],
      delay: anime.stagger(30),
      duration: 500,
      easing: 'easeOutCubic',
      ...options
    });
  }

  /**
   * 3D card flip
   */
  cardFlip(targets, options = {}) {
    if (!this.shouldAnimate()) return null;

    return anime({
      targets,
      rotateY: [0, 180],
      duration: 800,
      easing: 'easeInOutQuad',
      ...options
    });
  }

  /**
   * Magnetic button effect
   */
  magneticPull(element, mouseX, mouseY, strength = 0.3) {
    if (!this.shouldAnimate()) return;

    const rect = element.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const deltaX = (mouseX - centerX) * strength;
    const deltaY = (mouseY - centerY) * strength;

    anime({
      targets: element,
      translateX: deltaX,
      translateY: deltaY,
      duration: 300,
      easing: 'easeOutCubic'
    });
  }

  /**
   * Reset magnetic effect
   */
  magneticReset(element) {
    if (!this.shouldAnimate()) return;

    anime({
      targets: element,
      translateX: 0,
      translateY: 0,
      duration: 500,
      easing: 'easeOutElastic(1, .6)'
    });
  }

  /**
   * Typing effect helper
   */
  typeText(element, text, speed = 50) {
    if (!this.shouldAnimate()) {
      element.textContent = text;
      return;
    }

    let index = 0;
    const interval = setInterval(() => {
      if (index < text.length) {
        element.textContent = text.substring(0, index + 1);
        index++;
      } else {
        clearInterval(interval);
      }
    }, speed);

    return interval;
  }

  /**
   * Ripple effect
   */
  ripple(x, y, container) {
    if (!this.shouldAnimate()) return;

    const ripple = document.createElement('div');
    ripple.style.position = 'absolute';
    ripple.style.width = '10px';
    ripple.style.height = '10px';
    ripple.style.borderRadius = '50%';
    ripple.style.background = 'rgba(0, 255, 65, 0.5)';
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.style.transform = 'translate(-50%, -50%)';
    ripple.style.pointerEvents = 'none';
    
    container.appendChild(ripple);

    anime({
      targets: ripple,
      scale: [0, 30],
      opacity: [0.6, 0],
      duration: 600,
      easing: 'easeOutQuad',
      complete: () => ripple.remove()
    });
  }

  /**
   * Shake animation
   */
  shake(targets, options = {}) {
    if (!this.shouldAnimate()) return null;

    return anime({
      targets,
      translateX: [
        { value: -10, duration: 100 },
        { value: 10, duration: 100 },
        { value: -10, duration: 100 },
        { value: 10, duration: 100 },
        { value: 0, duration: 100 }
      ],
      easing: 'easeInOutSine',
      ...options
    });
  }

  /**
   * Float animation
   */
  float(targets, options = {}) {
    if (!this.shouldAnimate()) return null;

    return anime({
      targets,
      translateY: [0, -10, 0],
      duration: 3000,
      loop: true,
      easing: 'easeInOutSine',
      ...options
    });
  }
}

// Export singleton instance
const animationManagerInstance = new AnimationManager();
export default animationManagerInstance;
